import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Utility
 */
const isNeg = (v) => typeof v === "string" && v.trim().startsWith("-");
const cellColor = (v) => (isNeg(v) ? "text-red-600" : "text-emerald-600");

/**
 * DATA MODEL
 */
const STRATEGIES = [
  {
    key: "aegis",
    label: "Aegis",
    subtitle: "High-conviction momentum execution",
    profitability: {
      2021: {
        Jan: "0.00%", Feb: "33.74%", Mar: "-17.31%", Apr: "46.73%",
        May: "-0.99%", Jun: "30.98%", Jul: "56.45%", Aug: "1.20%",
        Sep: "21.84%", Oct: "45.62%", Nov: "1.82%", Dec: "30.24%",
        Total: "32.60%",
      },
      2022: {
        Jan: "12.16%", Feb: "25.64%", Mar: "36.71%", Apr: "19.78%",
        May: "21.30%", Jun: "60.67%", Jul: "3.14%", Aug: "31.85%",
        Sep: "32.37%", Oct: "45.62%", Nov: "-0.37%", Dec: "4.01%",
        Total: "512.53%",
      },
      2023: {
        Jan: "34.10%", Feb: "2.99%", Mar: "127.50%", Apr: "23.39%",
        May: "4.59%", Jun: "27.85%", Jul: "-6.04%", Aug: "20.17%",
        Sep: "-7.08%", Oct: "36.58%", Nov: "-3.71%", Dec: "17.77%",
        Total: "742.42%",
      },
      2024: {
        Jan: "2.05%", Feb: "98.92%", Mar: "0.00%", Apr: "4.55%",
        May: "3.27%", Jun: "13.04%", Jul: "50.41%", Aug: "24.54%",
        Sep: "3.72%", Oct: "23.19%", Nov: "0.00%", Dec: "8.00%",
        Total: "540.43%",
      },
      2025: {
        Jan: "-", Feb: "0.10%", Mar: "0.44%", Apr: "-12.68%",
        May: "2.06%", Jun: "-9.56%", Jul: "19.77%", Aug: "-19.73%",
        Sep: "1.19%", Oct: "-0.81%", Nov: "-", Dec: "-",
        Total: "-",
      },
    },
    performance: [
      { label: "Total Return", value: "473,613.06%" },
      { label: "Winning Months", value: "86.00%" },
      { label: "Average Annual Return", value: "695.05%" },
      { label: "Average Monthly Return", value: "26.90%" },
      { label: "Maximum Monthly Return", value: "127.50%" },
      { label: "Sharpe Ratio", value: "4.15" },
      { label: "Sortino Ratio", value: "6.40" },
      { label: "Kalmar Ratio", value: "5.24" },
    ],
    risk: [
      { label: "Maximum Drawdown", value: "30.39%" },
      { label: "Maximum Monthly Loss", value: "17.31%" },
      { label: "Average Monthly Loss", value: "5.20%" },
      { label: "Return / Risk", value: "22.87" },
      { label: "Std Dev of Monthly Returns", value: "25.99%" },
    ],
  },

  {
    key: "athena",
    label: "Athena",
    subtitle: "Multi-layer trend capture",
    profitability: {
      2021: {
        Jan: "0.33%", Feb: "24.18%", Mar: "-11.42%", Apr: "20.68%",
        May: "-0.07%", Jun: "12.47%", Jul: "21.63%", Aug: "-0.32%",
        Sep: "25.99%", Oct: "26.42%", Nov: "-1.96%", Dec: "23.21%",
        Total: "24.74%",
      },
      2022: {
        Jan: "11.19%", Feb: "20.04%", Mar: "17.18%", Apr: "10.55%",
        May: "6.89%", Jun: "30.30%", Jul: "6.68%", Aug: "8.10%",
        Sep: "9.71%", Oct: "20.39%", Nov: "-2.23%", Dec: "3.17%",
        Total: "192.34%",
      },
      2023: {
        Jan: "11.69%", Feb: "1.58%", Mar: "75.26%", Apr: "32.68%",
        May: "-3.18%", Jun: "42.80%", Jul: "-12.85%", Aug: "2.21%",
        Sep: "-12.07%", Oct: "26.52%", Nov: "-4.02%", Dec: "7.73%",
        Total: "256.48%",
      },
      2024: {
        Jan: "0.13%", Feb: "53.73%", Mar: "0.00%", Apr: "11.37%",
        May: "29.73%", Jun: "11.66%", Jul: "29.85%", Aug: "19.25%",
        Sep: "-3.72%", Oct: "14.17%", Nov: "-0.21%", Dec: "28.86%",
        Total: "443.54%",
      },
      2025: {
        Jan: "-", Feb: "-", Mar: "-1.61%", Apr: "-18.08%",
        May: "-1.77%", Jun: "-7.63%", Jul: "26.36%", Aug: "-18.86%",
        Sep: "-2.22%", Oct: "-1.05%", Nov: "-", Dec: "-",
        Total: "-",
      },
    },
    performance: [
      { label: "Total Return", value: "26,308.42%" },
      { label: "Winning Months", value: "76.00%" },
      { label: "Average Annual Return", value: "291.52%" },
      { label: "Average Monthly Return", value: "18.91%" },
      { label: "Maximum Monthly Return", value: "75.26%" },
      { label: "Sharpe Ratio", value: "3.41" },
      { label: "Sortino Ratio", value: "5.28" },
      { label: "Kalmar Ratio", value: "4.73" },
    ],
    risk: [
      { label: "Maximum Drawdown", value: "25.62%" },
      { label: "Maximum Monthly Loss", value: "12.85%" },
      { label: "Average Monthly Loss", value: "4.39%" },
      { label: "Return / Risk", value: "11.38" },
      { label: "Std Dev of Monthly Returns", value: "16.73%" },
    ],
  },

  {
    key: "orion",
    label: "Orion",
    subtitle: "Long-term momentum framework",
    profitability: {
      2019: {
        Jan: "0.63%", Feb: "7.57%", Mar: "1.45%", Apr: "23.27%",
        May: "7.02%", Jun: "11.24%", Jul: "0.55%", Aug: "12.12%",
        Sep: "2.13%", Oct: "23.9%", Nov: "7.28%", Dec: "-2.94%",
        Total: "139.38%",
      },
      2020: {
        Jan: "10.89%", Feb: "1.44%", Mar: "10.66%", Apr: "15.42%",
        May: "3.99%", Jun: "-5.88%", Jul: "22.69%", Aug: "-0.03%",
        Sep: "7.26%", Oct: "2.80%", Nov: "0.67%", Dec: "13.46%",
        Total: "117.25%",
      },
      2021: {
        Jan: "0.31%", Feb: "13.39%", Mar: "-5.93%", Apr: "6.34%",
        May: "0.22%", Jun: "3.02%", Jul: "4.47%", Aug: "-0.65%",
        Sep: "18.51%", Oct: "12.13%", Nov: "-1.76%", Dec: "1.87%",
        Total: "62.15%",
      },
      2022: {
        Jan: "7.18%", Feb: "11.76%", Mar: "5.87%", Apr: "4.40%",
        May: "0.48%", Jun: "11.52%", Jul: "5.46%", Aug: "-1.39%",
        Sep: "0.00%", Oct: "-0.32%", Nov: "12.18%", Dec: "-4.36%",
        Total: "65.00%",
      },
      2023: {
        Jan: "1.39%", Feb: "0.65%", Mar: "35.25%", Apr: "24.44%",
        May: "-4.34%", Jun: "32.80%", Jul: "-10.51%", Aug: "-3.66%",
        Sep: "-9.47%", Oct: "14.81%", Nov: "-2.77%", Dec: "2.28%",
        Total: "94.44%",
      },
      2024: {
        Jan: "-0.46%", Feb: "22.91%", Mar: "0.00%", Apr: "9.53%",
        May: "27.38%", Jun: "7.38%", Jul: "14.03%", Aug: "11.32%",
        Sep: "-4.61%", Oct: "6.87%", Nov: "-0.20%", Dec: "25.20%",
        Total: "196.37%",
      },
      2025: {
        Jan: "-", Feb: "-", Mar: "-1.50%", Apr: "-8.95%",
        May: "-2.18%", Jun: "-4.41%", Jul: "12.53%", Aug: "-8.14%",
        Sep: "-1.02%", Oct: "-1.02%", Nov: "-", Dec: "-",
        Total: "-",
      },
    },
    performance: [
      { label: "Total Return", value: "48,810.56%" },
      { label: "Winning Months", value: "76.32%" },
      { label: "Average Annual Return", value: "112.63%" },
      { label: "Average Monthly Return", value: "10.04%" },
      { label: "Maximum Monthly Return", value: "35.25%" },
      { label: "Sharpe Ratio", value: "3.04" },
      { label: "Sortino Ratio", value: "4.19" },
      { label: "Kalmar Ratio", value: "3.71" },
    ],
    risk: [
      { label: "Maximum Drawdown", value: "26.97%" },
      { label: "Maximum Monthly Loss", value: "10.51%" },
      { label: "Average Monthly Loss", value: "3.75%" },
      { label: "Return / Risk", value: "4.17" },
      { label: "Std Dev of Monthly Returns", value: "9.69%" },
    ],
  },

  {
    key: "atlas",
    label: "Atlas",
    subtitle: "High-conviction BTC trend capture",
    profitability: {
      2023: {
        Jan: "8.34%", Feb: "19.85%", Mar: "1.61%", Apr: "-4.19%",
        May: "-4.19%", Jun: "2.80%", Jul: "-4.20%", Aug: "26.49%",
        Sep: "-11.02%", Oct: "44.50%", Nov: "18.41%", Dec: "0.00%",
        Total: "139.74%",
      },
      2024: {
        Jan: "14.55%", Feb: "58.02%", Mar: "7.39%", Apr: "-8.06%",
        May: "8.51%", Jun: "-3.81%", Jul: "2.35%", Aug: "10.29%",
        Sep: "13.97%", Oct: "7.82%", Nov: "20.13%", Dec: "-4.08%",
        Total: "198.16%",
      },
      2025: {
        Jan: "-", Feb: "-", Mar: "-1.48%", Apr: "9.98%",
        May: "11.40%", Jun: "-13.51%", Jul: "1.89%", Aug: "4.65%",
        Sep: "-2.97%", Oct: "-6.53%", Nov: "-", Dec: "-",
        Total: "-",
      },
    },
    performance: [
      { label: "Total Return", value: "615.59%" },
      { label: "Winning Months", value: "73.91%" },
      { label: "Average Annual Return", value: "168.95%" },
      { label: "Average Monthly Return", value: "16.56%" },
      { label: "Maximum Monthly Return", value: "58.02%" },
      { label: "Sharpe Ratio", value: "3.41" },
      { label: "Sortino Ratio", value: "5.28%" },
      { label: "Kalmar Ratio", value: "4.73" },
    ],
    risk: [
      { label: "Maximum Drawdown", value: "20.44%" },
      { label: "Maximum Monthly Loss", value: "11.02%" },
      { label: "Average Monthly Loss", value: "5.89%" },
      { label: "Return / Risk", value: "8.27" },
      { label: "Std Dev of Monthly Returns", value: "16.06%" },
    ],
  },
];

/**
 * TABLE HEADERS (months)
 */
const MONTH_ORDER = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export default function ProofOfPerformance() {
  const [active, setActive] = useState(0);
  const [expandedYear, setExpandedYear] = useState(null);
  const s = STRATEGIES[active];

  return (
    <section id="performance" className="py-24 px-6 bg-neutral-50">
      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Performance verified on-chain.
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
            Partner with QuantNexus Capital to integrate your strategies, custody solutions, or institutional frameworks into our automated vault infrastructure.
            Together, we create the connective layer between traditional finance and decentralized markets.
          </p>

          {/* NEW NOTE */}
          <motion.p
            className="text-sm text-gray-500 max-w-2xl mx-auto italic"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            These results represent performance data for{" "}
            <span className="font-semibold text-cyan-700">4 of 12</span> 
            {" "}active strategies currently available for allocation. 
            Contact us to request details on the remaining strategies and 
            institutional portfolios.
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-white border border-gray-200 p-1.5 rounded-full shadow-sm">
            {STRATEGIES.map((st, i) => (
              <button
                key={st.key}
                onClick={() => {
                  setActive(i);
                  setExpandedYear(null);
                }}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  active === i
                    ? "bg-cyan-600 text-white shadow-md"
                    : "text-gray-600 hover:text-cyan-700"
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-center text-sm text-gray-500 mb-8">{s.subtitle}</p>

        {/* Profitability Section */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6 md:p-8 mb-12">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Profitability</h3>

          {Object.entries(s.profitability).map(([year, months]) => {
            const hasMonths = MONTH_ORDER.some((m) => months[m]);
            const isOpen = expandedYear === year;

            return (
              <div key={year} className="mb-4">
                <button
                  onClick={() => setExpandedYear(isOpen ? null : year)}
                  className="w-full flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-4 rounded-lg transition"
                >
                  <span className="font-medium text-gray-800">{year}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-xs">
                      {hasMonths ? (isOpen ? "Hide months" : "Show months") : "Yearly total"}
                    </span>
                    <span className="font-bold text-cyan-700">{months.Total}</span>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && hasMonths && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35 }}
                      className="overflow-hidden"
                    >
                      <div className="overflow-x-auto">
                        <table className="w-full mt-4 text-sm">
                          <thead>
                            <tr className="text-gray-500">
                              {MONTH_ORDER.filter((m) => months[m]).map((m) => (
                                <th key={m} className="px-2 py-1 text-left">
                                  {m}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              {MONTH_ORDER.filter((m) => months[m]).map((m) => (
                                <td
                                  key={m}
                                  className={`px-2 py-1 ${cellColor(months[m])}`}
                                >
                                  {months[m]}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Indicators */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-gray-200 rounded-xl shadow-md p-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Indicators</h4>
            <ul className="space-y-2">
              {s.performance.map((p, i) => (
                <li key={i} className="flex justify-between text-gray-700">
                  <span>{p.label}</span>
                  <span className="font-semibold text-cyan-700">{p.value}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-md p-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk Indicators</h4>
            <ul className="space-y-2">
              {s.risk.map((r, i) => (
                <li key={i} className="flex justify-between text-gray-700">
                  <span>{r.label}</span>
                  <span className="font-semibold text-red-600">{r.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="mt-10 text-center text-xs text-gray-400 italic">
          Past performance is not indicative of future results. Strategy figures reflect
          live-tracked performance with on-chain fee enforcement above the high-watermark.
        </p>
      </div>
    </section>
  );
}
